import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Select, {
  ActionMeta,
  ControlProps,
  MultiValue,
  SingleValue,
  components,
} from "react-select";
import { formatTimestampToAgo, secondsToHms, useDebounce } from "../../utils";

import { generateBaseUrl, getThumbnailUrlUtil } from "@/utils/mediaUtils";
import { httpCall } from "../../utils";
import { MediaModel, InputWithMediaInfoRef } from "@/database";
import noImage from "../../assets/icons/svg/no-image.svg";
// import { getSearchedListMedia } from '../../../../../../apps/slike-cms/src/store/media/media.action';
import { RootState } from "../../store";
import { connect, ConnectedProps } from "react-redux";
import { GeneralPlayer } from "../CleoPlayer/GeneralPlayer";
import Modal from "../Modal/Modal";
import Search from "../../assets/icons/svg/search.svg?react";
import { truncateText } from "@/utils/utilFunctions/truncateText";
import { PublishedIcon } from "../PublishedIcons/PublishedIcons";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

export interface MediaOption {
  label: string;
  value: number | string | string[] | MediaOption[] | number[];
  image?: string;
  duration?: string;
  thumb?: boolean;
}

interface axiosResponse {
  result: any;
  code: number;
  erorr: string;
}

interface SelectProps {
  options?: MediaModel[];
  name?: string;
  handleChange?: (
    selectedOption: SingleValue<MediaOption> | MultiValue<MediaOption>,
    actionMeta: ActionMeta<MediaOption>
  ) => void;
  value: number | string | string[] | MediaOption[] | number[];
  placeholder?: React.ReactNode | string;
  label?: string;
  disabled?: boolean;
  className?: string;
  multiSelect?: boolean;
  handleClear?: any;
  showRequired?: boolean;
  error?: string;
  touched?: boolean;
  handleBlur?: (e: any) => void;
  showHeader?: boolean;
  hostSubSection?: boolean;
  menuPlacement?: "auto" | "bottom" | "top";
  customStyling?: React.CSSProperties;
  loaderRight?: string;

  // for suggestions
  apiUrl?: string;
  apiRequestBody?: Record<any, any>;
  rpcCallMethod?: string;
  apiCallMethod?: HttpMethod;
  apiResponseFormatter?: any;
  rpcCallParams?: Record<any, any>;
  groupPosition?: "left" | "right";

  //for making API call when input changes
  onInputChangeCallback?: (params: { hostid: string; keyword: string }) => void;
  callbackParams?: any;
  hideArrow?: boolean;

  // ✅ Dynamic width & height props
  width?: string | number;
  height?: string | number;
  onDebouncedInputChange?: (inputValue: string) => void;
  menuOpen?: boolean;
  initialSelectedOption?: any;
  right?: string;
  initialStructuredData?: any;
  loader?: boolean;
  menuPortalTarget?: HTMLElement | null;
  menuStyles?: React.CSSProperties;
  maxMenuHeight?: number;
  initialInputText?: string;

  // getSearchedListMedia?: (stitle: string) => void;
}

export const InputWithMediaInfo = forwardRef<
  InputWithMediaInfoRef,
  SelectProps
>(
  (
    {
      disabled,
      name,
      options = [],
      label,
      value,
      handleChange,
      placeholder = "Select an option",
      className,
      multiSelect = false,
      showRequired,
      touched,
      error,
      handleBlur,
      showHeader,
      apiUrl,
      apiCallMethod = "POST",
      rpcCallMethod,
      onInputChangeCallback,
      onDebouncedInputChange,
      menuPlacement,
      callbackParams,
      hideArrow,
      width = "100%", // ✅ Default width to 100%
      height = "38px", // ✅ Default height to 38px (like Bootstrap input)
      menuOpen,
      initialSelectedOption,
      initialStructuredData,
      customStyling,
      loader = false,
      loaderRight = "0",
      menuPortalTarget = document.body,
      menuStyles,
      maxMenuHeight,
      initialInputText = "",
    },
    ref
  ) => {
    const [suggestedOptions, setSuggestedOptions] = useState<MediaOption[]>([]);
    const suggestedOptionsRef = useRef<MediaOption[]>([]);
    const [inputText, setInputText] = useState<string>(initialInputText);
    const debouncedInputText = useDebounce(inputText, 200);
    const [selectedOptions, setSelectedOptions] = useState<any>(
      initialStructuredData ? initialStructuredData : null
    );

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modalTitle, setModalTitle] = useState<string>("");
    const [modalContent, setModalContent] = useState<React.ReactNode | null>(
      null
    );
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingComplete, setIsLoadingComplete] = useState(false);

    const selectRef = useRef<any>(null);
    const [menuState, setMenuState] = useState<boolean>(!!menuOpen);

    useImperativeHandle(ref, () => ({
      openMenu: () => {
        selectRef.current.focus();
        setTimeout(() => {
          selectRef.current.setState?.({ menuIsOpen: true });
          setMenuState(true);
        }, 0);
      },
    }));

    const handleModalClose = () => {
      setIsModalOpen(false);
      setModalContent(null);
      setModalTitle("");
    };

    if (selectedOptions && initialSelectedOption) {
      options = [...options, initialSelectedOption];
    }
    // if (options.length === 0) {
    //   return;
    // }

    let fetchedOptions = options.map((media: any) => ({
      label: media?.id,
      value: media?.title,
      image:
        getThumbnailUrlUtil({
          media,
          id: media?.images?.thumb || media?.images?.portrait,
          source: media?.source,
        }) || "",
      duration: secondsToHms(media.meta?.duration),
      thumb: !generateBaseUrl(media) ? false : true,
      uploadedAt: media?.ct ? formatTimestampToAgo(media?.ct) : "",
      publishedPlatforms: [
        ...new Set(
          (media?.socials ?? []).map((s: any) => s?.job_type).filter(Boolean)
        ),
      ] as string[],
    }));

    if (fetchedOptions.length > 0) {
      if (fetchedOptions[0]?.label === undefined) {
        fetchedOptions = [];
        return;
      }
    }

    useEffect(() => {
      if (loader === false) return;
      if (inputText) {
        const hasMatchingOption = fetchedOptions.some((option) =>
          option?.value?.toLowerCase().includes(inputText.toLowerCase())
        );
        if (!hasMatchingOption) {
          setIsLoading(true);
        } else {
          setIsLoading(false);
          setIsLoadingComplete(true);
        }
      }
    }, [inputText, fetchedOptions, loader]);

    const CustomDropdownIndicator = (props: any) => {
      if (props.selectProps.hideArrow) return null;
      return <components.DropdownIndicator {...props} />;
    };

    const CustomSingleValue = () => null;

    const CustomOption = (props: any) => {
      const { data, innerRef, innerProps, isFocused, isSelected } = props;
      const [imgFailed, setImgFailed] = useState(false);
      const showImage = data.thumb && !!data.image && !imgFailed;

      return (
        <div
          ref={innerRef}
          {...innerProps}
          className="d-flex catRow pointer"
          style={{
            gap: "10px",
            padding: "8px 12px",
            borderBottom: "1px solid #f0f2f5",
            backgroundColor: isSelected
              ? "#e7f1ff"
              : isFocused
                ? "#f7f9fc"
                : "#fff",
          }}
        >
          <div
            className="flex-shrink-0 position-relative"
            style={{
              width: "116px",
              minWidth: "116px",
              height: "65px",
              borderRadius: 8,
              overflow: "hidden",
              background: showImage
                ? "#ffffff"
                : "linear-gradient(135deg, #e0e7ef 0%, #c8d3e0 100%)",
              boxShadow:
                "0 2px 6px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.08)",
            }}
          >
            {showImage ? (
              <img
                src={data.image}
                alt={data.label}
                loading="lazy"
                className="position-absolute top-0 start-0 w-100 h-100"
                style={{
                  borderRadius: 8,
                  objectFit: "cover",
                  objectPosition: "center",
                  display: "block",
                }}
                onError={() => setImgFailed(true)}
              />
            ) : (
              <img
                src={noImage}
                alt={data.label}
                className="position-absolute top-50 start-50 translate-middle"
                style={{ width: "32px", opacity: 0.5 }}
              />
            )}
            {data.duration && (
              <span
                style={{
                  position: "absolute",
                  right: 4,
                  bottom: 4,
                  background: "rgba(0,0,0,0.78)",
                  color: "#fff",
                  fontSize: "10px",
                  fontWeight: 600,
                  padding: "1px 5px",
                  borderRadius: 3,
                  lineHeight: 1.25,
                  letterSpacing: "0.02em",
                  backdropFilter: "blur(2px)",
                }}
              >
                {data.duration}
              </span>
            )}
          </div>

          <div className="flex-grow-1 min-w-0 d-flex flex-column">
            <div
              className="fw-semibold text-dark"
              style={{
                fontSize: "12.5px",
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
              }}
              title={String(data.value || "")}
            >
              {truncateText(String(data.value || ""), 45)}
            </div>
            <div
              className="text-muted d-flex align-items-center flex-wrap"
              style={{
                fontSize: "10.5px",
                gap: "2px",
                lineHeight: 1.2,
                marginTop: 3,
              }}
            >
              <span style={{ fontFamily: "ui-monospace, monospace" }}>
                Uploaded:
              </span>
              {data.uploadedAt && (
                <>
                  <span>{data.uploadedAt}</span>
                </>
              )}
            </div>
            {data.publishedPlatforms?.length > 0 && (
              <div className="d-flex gap-1 mt-1 flex-wrap">
                {data.publishedPlatforms.map((job_type: string) => (
                  <PublishedIcon
                    key={job_type}
                    job_type={job_type}
                    status={10}
                    size={14}
                    showQuestionCircle={false}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      );
    };

    useEffect(() => {
      if (!debouncedInputText) {
        setSuggestedOptions([]); // Only clear if input is empty
        return;
      }
      if (debouncedInputText && onDebouncedInputChange) {
        onDebouncedInputChange(debouncedInputText);
      }
      if (onInputChangeCallback && debouncedInputText.length > 0) {
        onInputChangeCallback?.({
          keyword: debouncedInputText,
          hostid: callbackParams,
        });
      } else if (apiUrl && inputText) {
        (async () => {
          try {
            const response = await httpCall<axiosResponse>({
              url: apiUrl,
              method: apiCallMethod,
              data: {
                ...(rpcCallMethod && { method: rpcCallMethod }),
                ...(rpcCallMethod && { params: { title: inputText } }),
                ...(!rpcCallMethod && { value: inputText }),
              },
            });
            if (response.code === 200) {
              const fetchedOptions = response.result.list.map((media: any) => ({
                label: media.title,
                value: media.id,
              }));
              const previousSuggestedOptions = multiSelect
                ? suggestedOptionsRef.current.filter((option) => {
                    //@ts-ignore
                    if (value.includes(option.value)) return true;
                    return false;
                  })
                : [];

              suggestedOptionsRef.current = [
                ...new Set([...fetchedOptions, ...previousSuggestedOptions]),
              ];
              setSuggestedOptions(suggestedOptionsRef.current);
            }
          } catch (error) {
            console.error("Error fetching options:", error);
          }
        })();
      }
    }, [debouncedInputText, apiUrl, callbackParams]);

    const o = apiUrl ? suggestedOptions : options;
    // const selectOptions = label === 'Parent Video' ? fetchedOptions : apiUrl ? suggestedOptions : options;
    const valueInOptionFormat = multiSelect
      ? //@ts-ignore
        fetchedOptions.filter(
          (option) =>
            Array.isArray(value) &&
            (value as (string | number)[])?.includes(
              option.value as string | number
            )
        )
      : fetchedOptions.find((option) => option.label === value) || null;

    const CustomControl = (props: ControlProps<any>) => (
      <components.Control {...props}>
        <i className="bi bi-search ms-2"></i>
        {props.children}
      </components.Control>
    );
    useEffect(() => {
      if (value) {
        setInputText(""); // ✅ Reset input after value change
      }
    }, [value]);

    useEffect(() => {
      if (initialInputText) {
        setInputText(initialInputText);
      }
    }, [initialInputText]);

    return (
      <>
        {isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            title={modalTitle}
            width="770px"
          >
            {modalContent}
          </Modal>
        )}
        <div className={`${className} position-relative`}>
          {showHeader !== false && (
            <div className="d-flex align-items-center gap-1">
              <label className="p-1">{label}</label>
              {showRequired && <span className="required mr-2">*</span>}
            </div>
          )}
          <Select
            menuPortalTarget={menuPortalTarget}
            {...(maxMenuHeight !== undefined && { maxMenuHeight })}
            styles={{
              placeholder: (provided: any) => ({
                ...provided,
                color: "#9e9e9e",
              }),
              menuPortal: (base) => {
                const menuWidth = 440;
                const rawLeft = base.left as number;
                const clampedLeft = Math.max(
                  8,
                  Math.min(rawLeft, window.innerWidth - menuWidth - 8)
                );
                return { ...base, zIndex: 9999, left: clampedLeft };
              },
              menu: (base) => ({
                ...base,
                minWidth: "440px",
                ...(menuStyles || {}),
              }),
              control: (base) => ({
                ...base,
                width: typeof width === "number" ? `${width}px` : width, // ✅ Dynamic width
                height: typeof height === "number" ? `${height}px` : height, // ✅ Dynamic height
                minHeight: typeof height === "number" ? `${height}px` : height,
                padding: "2px 8px", // ✅ Proper padding to center the value
                display: "flex",
                alignItems: "center",
                ...(customStyling || {}),
              }),
              valueContainer: (base) => ({
                ...base,
                padding: "0 8px", // ✅ Remove unnecessary padding
              }),
              singleValue: (base) => ({
                ...base,
                display: "flex",
                alignItems: "center", // ✅ Proper alignment of value
                height: "100%",
                paddingLeft: 0,
                marginLeft: 0,
                margin: 0,
              }),
              indicatorSeparator: (base) => ({
                ...base,
                display: "none", // ✅ Hide the vertical line
              }),
            }}
            name={name}
            isMulti={multiSelect}
            classNamePrefix="react-select"
            ref={selectRef}
            menuIsOpen={menuState}
            onMenuOpen={() => setMenuState(true)}
            onMenuClose={() => setMenuState(false)}
            isDisabled={disabled}
            options={fetchedOptions}
            value={valueInOptionFormat}
            inputValue={inputText}
            filterOption={() => true}
            noOptionsMessage={() => {
              if (loader === false) return null;
              if (!inputText) return null;
              if (!isLoadingComplete) return null;
              const hasMatchingOption = fetchedOptions.some((option) =>
                option.value.toLowerCase().includes(inputText.toLowerCase())
              );
              return hasMatchingOption ? null : "No Matches Found";
            }}
            menuPlacement={menuPlacement || "auto"}
            menuPosition="fixed"
            isSearchable={!disabled}
            // noOptionsMessage={() => null}
            {...(apiUrl && {
              onInputChange: (value, action) => {
                setInputText(value);
                if (value && onInputChangeCallback) {
                  onInputChangeCallback({
                    keyword: value,
                    hostid: callbackParams,
                  });
                }
              },
            })}
            onInputChange={(value, action) => {
              if (
                action.action === "input-blur" ||
                action.action === "menu-close"
              )
                return;

              setInputText(value);

              setIsLoadingComplete(false);
              if (loader && value) {
                setIsLoading(true);
              } else {
                setIsLoading(false);
              }

              if (value && onInputChangeCallback) {
                onInputChangeCallback({
                  keyword: value,
                  hostid: callbackParams,
                });
              }
            }}
            onChange={(selectedOption, actionMeta) => {
              handleChange && handleChange(selectedOption, actionMeta);
              setSelectedOptions(selectedOption);
              setInputText("");
            }}
            onBlur={handleBlur}
            isClearable
            placeholder={placeholder}
            components={{
              ...(apiUrl && { Control: CustomControl }),
              SingleValue: CustomSingleValue,
              // ...(label === 'Parent Video' && { Option: CustomOption }),
              Option: CustomOption,
              ...(hideArrow && { DropdownIndicator: () => null }),
              ...(!hideArrow && { DropdownIndicator: CustomDropdownIndicator }),
            }}
          />
          {loader && isLoading && inputText && !isLoadingComplete && (
            <div
              className={`position-absolute bottom-0 start-${loaderRight} w-100`}
              style={{ height: "2px" }}
            >
              <div
                key={inputText}
                className="bg-primary"
                style={{
                  width:
                    loaderRight === "0"
                      ? "100%"
                      : `calc(100% - ${loaderRight})`,
                  height: "100%",
                  animation: "progress 2s ease-in-out forwards",
                  background: "#0d6efd",
                  transformOrigin: "left",
                  transform: "scaleX(0)",
                }}
                onAnimationEnd={() => {
                  setIsLoadingComplete(true);
                  setIsLoading(false);
                }}
              />
              <style>
                {`
                @keyframes progress {
                  0% {
                    transform: scaleX(0);
                  }
                  100% {
                    transform: scaleX(1);
                  }
                }
              `}
              </style>
            </div>
          )}
          {/* <span
            className="input-group-text pointer position-absolute"
            style={{ height: '37px', top: '25px', right: right }}
            onClick={handleShowPreview}
          >
            <i className="bi bi-play fs-6 pointer"></i>
          </span> */}

          {touched && error && (
            <div className="form-text text-danger px-2">{error}</div>
          )}
        </div>
      </>
    );
  }
);

InputWithMediaInfo.displayName = "InputWithMediaInfo";

const mapState = (state: RootState) => ({
  mediaList: state.media.mediaList.list,
});
const mapDispatch = {
  // getSearchedListMedia,
};
const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;
export default connector(InputWithMediaInfo);

const RenderModalComponent = ({ videoId }: { videoId: string }) => {
  return (
    <div style={{ height: "33.75vw", width: "auto" }}>
      <GeneralPlayer id={videoId} />
    </div>
  );
};
